import { makeStyles } from '@material-ui/styles';

import {
    OutlookCalendarIcon,
    GoogleCalendarIcon,
    ICalendarIcon,
    GraphicButton,
    CloseFill,
    BaseButton,
    Text,
} from '@bizzabo/design-system';
import { UserPreview } from '@bizzabo/entities';

import { getAddToCalendarUrl } from '../../../../helpers/addToCalendar';
import useTypeTracking from '../../../../analytics';
import { ScheduledMeeting } from '../../types';
import { useMeetings } from '../../MeetingsTool/MeetingsToolContext';

const calendars = {
    outlook: { title: 'Outlook', icon: OutlookCalendarIcon },
    google: { title: 'Google Calendar', icon: GoogleCalendarIcon },
    ical: { title: 'iCal', icon: ICalendarIcon },
};

type Props = {
    meeting: ScheduledMeeting;
    otherUser: UserPreview;
    onClose: () => void;
};

const AddToCalendar: React.FC<Props> = ({ meeting, otherUser, onClose }) => {
    const { trackClickEvent } = useTypeTracking();
    const { event } = useMeetings();
    const classes = useStyles();
    const title = `${event?.name}: meeting with ${otherUser.firstName} ${otherUser.lastName}`;

    const doAddToCalendar = (calendarType: string) => {
        const calendarUrl = getAddToCalendarUrl({
            type: calendarType,
            startDateUTCTimestamp: meeting.startDate.getTime(),
            endDateUTCTimestamp: meeting.endDate.getTime(),
            title,
            description: meeting.description,
        });

        window.open(calendarUrl, '_blank');

        trackClickEvent({
            subCategory: 'CALENDAR_PICKED',
            description: {
                calendar_name: `${calendarType.toLocaleUpperCase()}_CALENDAR`,
            },
        });
    };

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <Text variant="t60">Add to calendar</Text>
                <BaseButton className={classes.iconCloseButton} onClick={onClose}>
                    <CloseFill />
                </BaseButton>
            </div>
            <div className={classes.calendars}>
                {Object.keys(calendars).map(key => (
                    <GraphicButton
                        onClick={() => doAddToCalendar(key)}
                        icon={calendars[key].icon}
                        key={key}
                    >
                        {calendars[key].title}
                    </GraphicButton>
                ))}
            </div>
        </div>
    );
};

const useStyles = makeStyles(
    theme => ({
        root: {
            padding: 24,
            backgroundColor: theme.customization.background.C10,
            color: theme.customization.background.G10,
            borderRadius: 33,
            boxShadow: '0px 4px 14px rgba(64, 64, 64, 0.1)',
            width: 304,
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 24,
        },
        iconCloseButton: {
            fontSize: 16,
            fontWeight: 'bold',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        calendars: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
        },
    }),
    { classNamePrefix: 'add-to-calendar' }
);

export default AddToCalendar;
